'use client';

import { useRouter } from 'next/navigation';
import { useEffect, type ReactNode } from 'react';

import { AccountShell } from '@eliadmin/core/src/components/nt/account/account-shell';
import { GenericLoader } from '@eliadmin/core/src/components/ui/generic-loader';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { useLocalePathname } from '@eliadmin/core/src/hooks/use-locale-pathname';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { arePathsEqual } from '@eliadmin/core/src/utils/url-utils';

/** Account pages with the NajTonery design; the return forms stay core pages inside the shell. */
const ACCOUNT_ROUTES = () => [
  ROUTES.PROFILE(),
  ROUTES.CHANGE_PASSWORD(),
  ROUTES.ORDERS(),
  ROUTES.FAVORITES(),
  ROUTES.RETURN(),
];

/**
 * Gate of the profile URLs (Figma „Moje konto“ 201:63010). A guest goes to the sign-in page
 * (221:13120), which brings them back here afterwards – on „Obľúbené“ to the guest favourites page
 * instead (208:12680). Core profile URLs the design does not have (watchdogs, loyalty, settings …)
 * go to „Môj profil“.
 */
export function AccountGate({ children }: { children: ReactNode }) {
  const router = useRouter();
  const pathname = useLocalePathname();
  const me = useCustomerStore(s => s.me);
  const isInitialized = useCustomerStore(s => s.isInitialized);
  const known = ACCOUNT_ROUTES().some(route => arePathsEqual(route, pathname));
  const onFavorites = arePathsEqual(ROUTES.FAVORITES(), pathname);

  useEffect(() => {
    if (!isInitialized) return;
    if (!me && onFavorites) router.replace(ROUTES.FAVORITE_PRODUCTS());
    else if (!me) router.replace(ROUTES.SIGN_IN(window.location.pathname + window.location.search));
    else if (!known) router.replace(ROUTES.PROFILE());
  }, [isInitialized, known, me, onFavorites, router]);

  if (!isInitialized) return <GenericLoader />;
  if (!me || !known) return null;
  return <AccountShell>{children}</AccountShell>;
}
